"use client";

import { createFileRoute } from "@tanstack/react-router";
import { AnimatePresence, motion } from "framer-motion";
import type { LucideIcon } from "lucide-react";
import {
	Bell,
	Briefcase,
	BriefcaseBusiness,
	CalendarDays,
	ChefHat,
	Send,
	Sparkles,
	Upload,
	UtensilsCrossed,
	Wrench,
	X,
} from "lucide-react";
import { type FormEvent, useEffect, useId, useState } from "react";
import ReactMarkdown from "react-markdown";

import { useLang, useLangKey } from "@/contexts/lang";
import type { JobPosting } from "@/lib/jobPostings";
import { getJobPostings } from "@/lib/jobPostings";
import { fileToBase64, submitApplication } from "@/lib/submitApplication";

export const Route = createFileRoute("/career")({
	loader: async () => {
		const positions = await getJobPostings("mk-MK");
		return { positions };
	},
	headers: () => ({
		"Cache-Control":
			"public, max-age=0, s-maxage=3600, stale-while-revalidate=86400",
	}),
	head: () => ({
		meta: [
			{ title: "Кариера | Македонијатурист АД" },
			{
				name: "description",
				content:
					"Приклучете се на тимот на Македонијатурист АД. Отворени работни позиции во најголемата хотелска групација во Скопје. Градете ја вашата кариера кај нас.",
			},
		],
		links: [
			{
				rel: "canonical",
				href: "https://makedonijaturist.com.mk/career",
			},
		],
	}),
	component: CareerPage,
});

// ─── Category helpers ─────────────────────────────────────────────────────────

type CategoryId =
	| "reception"
	| "kitchen"
	| "restaurant"
	| "housekeeping"
	| "management"
	| "maintenance"
	| "events"
	| "general";

const categoryIcons: Record<CategoryId, LucideIcon> = {
	kitchen: ChefHat,
	reception: Bell,
	housekeeping: Sparkles,
	restaurant: UtensilsCrossed,
	management: BriefcaseBusiness,
	maintenance: Wrench,
	events: CalendarDays,
	general: Briefcase,
};

function getCategoryId(position: JobPosting): CategoryId {
	const t = position.title.toLowerCase();
	if (t.includes("готвач") || t.includes("chef")) return "kitchen";
	if (t.includes("рецепцион") || t.includes("аудитор")) return "reception";
	if (t.includes("собар")) return "housekeeping";
	if (t.includes("келнер") || t.includes("барист")) return "restaurant";
	if (t.includes("менаџер") || t.includes("директор")) return "management";
	if (t.includes("техничар")) return "maintenance";
	if (t.includes("координатор") || t.includes("настани")) return "events";
	return "general";
}

// ─── Main page ────────────────────────────────────────────────────────────────

export function CareerPageContent({ positions }: { positions: JobPosting[] }) {
	const t = useLang();
	const [applying, setApplying] = useState<JobPosting | null>(null);

	return (
		<main className="bg-background">
			{/* ── HERO ── */}
			<section className="mt-grain relative overflow-hidden bg-primary text-white">
				<div className="pointer-events-none absolute inset-0">
					<div className="absolute -top-1/4 right-[-8%] h-[600px] w-[600px] rounded-full bg-[radial-gradient(closest-side,rgba(201,161,90,0.1),transparent)] blur-[100px]" />
					<div className="absolute bottom-[-15%] left-[-5%] h-[450px] w-[450px] rounded-full bg-[radial-gradient(closest-side,rgba(255,255,255,0.05),transparent)] blur-[80px]" />
					<div className="absolute inset-0 bg-[linear-gradient(rgba(255,255,255,0.015)_1px,transparent_1px),linear-gradient(90deg,rgba(255,255,255,0.015)_1px,transparent_1px)] bg-[size:70px_70px] [mask-image:radial-gradient(ellipse_80%_80%_at_50%_50%,black_30%,transparent_100%)]" />
				</div>

				<div className="relative mx-auto max-w-7xl px-4 sm:px-6 lg:px-8 py-16 sm:py-20 md:py-32">
					<div className="grid lg:grid-cols-[1fr_300px] lg:items-center gap-12 lg:gap-16">
						<div>
							<motion.p
								initial={{ opacity: 0, y: 10 }}
								animate={{ opacity: 1, y: 0 }}
								transition={{ duration: 0.5 }}
								className="text-label text-accent mb-5"
							>
								{t.careerPage.hero.label}
							</motion.p>
							<motion.h1
								initial={{ opacity: 0, y: 18 }}
								animate={{ opacity: 1, y: 0 }}
								transition={{ duration: 0.65, delay: 0.08 }}
								className="font-display text-4xl sm:text-5xl md:text-6xl lg:text-7xl font-normal leading-[1.06] tracking-tight mb-6"
							>
								{t.careerPage.hero.heading1}
								<br />
								<span className="font-semibold text-accent">
									{t.careerPage.hero.heading2}
								</span>
							</motion.h1>
							<motion.p
								initial={{ opacity: 0, y: 14 }}
								animate={{ opacity: 1, y: 0 }}
								transition={{ duration: 0.6, delay: 0.16 }}
								className="text-base sm:text-lg text-white/65 leading-relaxed max-w-lg mb-10"
							>
								{t.careerPage.hero.intro}
							</motion.p>

							<motion.div
								initial={{ opacity: 0, y: 10 }}
								animate={{ opacity: 1, y: 0 }}
								transition={{ duration: 0.5, delay: 0.24 }}
								className="flex flex-wrap gap-8 border-t border-white/[0.08] pt-8"
							>
								{[
									{
										value: String(positions.length),
										label: t.careerPage.hero.openPositionsLabel,
									},
									{ value: "6", label: t.careerPage.hero.hotelsLabel },
									{
										value: t.aboutPage.hero.locationValue,
										label: t.careerPage.hero.locationLabel,
									},
								].map((stat) => (
									<div key={stat.label}>
										<div className="font-display text-2xl sm:text-3xl font-normal text-white mb-0.5">
											{stat.value}
										</div>
										<div className="text-xs font-semibold uppercase tracking-[0.16em] text-white/45">
											{stat.label}
										</div>
									</div>
								))}
							</motion.div>
						</div>

						{/* Decorative count ring */}
						<motion.div
							initial={{ opacity: 0, scale: 0.85 }}
							animate={{ opacity: 1, scale: 1 }}
							transition={{
								duration: 0.9,
								delay: 0.15,
								ease: [0.16, 1, 0.3, 1],
							}}
							className="hidden lg:flex items-center justify-center"
						>
							<div className="relative flex items-center justify-center w-56 h-56">
								<div className="absolute inset-0 rounded-full border border-white/[0.05]" />
								<div className="absolute inset-6 rounded-full border border-accent/[0.12]" />
								<div className="absolute inset-12 rounded-full border border-white/[0.04]" />
								<div className="relative flex flex-col items-center text-center">
									<span className="font-display text-7xl font-light text-accent leading-none">
										{positions.length}
									</span>
									<span className="text-[9px] font-bold tracking-[0.25em] uppercase text-white/40 mt-2">
										{t.careerPage.hero.positionsLabel}
									</span>
								</div>
								<motion.div
									animate={{ rotate: 360 }}
									transition={{
										duration: 20,
										repeat: Number.POSITIVE_INFINITY,
										ease: "linear",
									}}
									className="absolute inset-0"
								>
									<div className="absolute top-0 left-1/2 -translate-x-1/2 -translate-y-1/2 h-2 w-2 rounded-full bg-accent" />
								</motion.div>
							</div>
						</motion.div>
					</div>
				</div>
			</section>

			{/* ── POSITIONS ── */}
			<section className="bg-background py-14 sm:py-20 md:py-28 mt-paper">
				<div className="mx-auto max-w-7xl px-4 sm:px-6 lg:px-8">
					<div className="flex flex-col sm:flex-row sm:items-end justify-between gap-8 mb-10 sm:mb-14">
						<div>
							<span className="block h-px w-12 bg-accent mb-5" />
							<h2 className="font-display text-3xl sm:text-4xl md:text-5xl font-normal text-primary leading-[1.08] tracking-tight">
								{t.careerPage.positions.heading}
							</h2>
						</div>
					</div>

					<motion.div
						initial={{ opacity: 0 }}
						animate={{ opacity: 1 }}
						transition={{ duration: 0.18 }}
						className="grid gap-4 sm:gap-5 sm:grid-cols-2"
					>
						{positions.length > 0 ? (
							positions.map((position, idx) => (
								<PositionCard
									key={position.documentId}
									position={position}
									index={idx}
									onApply={() => setApplying(position)}
								/>
							))
						) : (
							<div className="sm:col-span-2 py-20 text-center text-foreground/40 text-sm">
								{t.careerPage.positions.noPositions}
							</div>
						)}
					</motion.div>
				</div>
			</section>

			{/* ── GENERAL INQUIRY ── */}
			<section className="mt-grain bg-primary py-16 sm:py-20 md:py-28 text-white">
				<div className="mx-auto max-w-7xl px-4 sm:px-6 lg:px-8">
					<div className="grid lg:grid-cols-[1fr_1fr] gap-12 lg:gap-20 lg:items-start">
						{/* Left: copy */}
						<div>
							<span className="block h-px w-12 bg-accent/50 mb-6" />
							<h2 className="font-display text-3xl sm:text-4xl md:text-5xl font-normal leading-[1.08] tracking-tight mb-6">
								{t.careerPage.inquiry.heading1}
								<span className="block mt-1 font-semibold text-white/50">
									{t.careerPage.inquiry.heading2}
								</span>
							</h2>
							<p className="text-base text-white/65 leading-relaxed mb-10 max-w-md">
								{t.careerPage.inquiry.intro}
							</p>

							<ul className="grid gap-4">
								{t.careerPage.inquiry.bullets.map((item) => (
									<li key={item} className="flex gap-3 items-start">
										<span className="mt-2 h-1 w-1 shrink-0 rounded-full bg-accent" />
										<p className="text-sm text-white/65 leading-relaxed">
											{item}
										</p>
									</li>
								))}
							</ul>
						</div>

						{/* Right: form */}
						<div className="relative overflow-hidden rounded-sm border border-white/[0.06] bg-white/[0.04] p-6 sm:p-8">
							<div className="pointer-events-none absolute right-0 top-0 h-20 w-20 opacity-30">
								<svg
									viewBox="0 0 80 80"
									fill="none"
									className="h-full w-full"
									aria-hidden="true"
								>
									<path
										d="M80 0 L80 80 L0 80"
										stroke="rgba(201,161,90,0.4)"
										strokeWidth="1"
										fill="none"
									/>
								</svg>
							</div>

							<div className="mb-5 h-px w-8 bg-accent/40" />
							<h3 className="font-display text-2xl font-normal text-white mb-1.5">
								{t.careerPage.inquiry.formHeading}
							</h3>
							<p className="mb-7 text-sm text-white/55">
								{t.careerPage.inquiry.formDesc}
							</p>

							<InquiryForm />
						</div>
					</div>
				</div>
			</section>

			{/* ── APPLY MODAL ── */}
			<AnimatePresence>
				{applying ? (
					<ApplyModal position={applying} onClose={() => setApplying(null)} />
				) : null}
			</AnimatePresence>
		</main>
	);
}

function CareerPage() {
	const { positions } = Route.useLoaderData();
	return <CareerPageContent positions={positions} />;
}

// ─── Position card ────────────────────────────────────────────────────────────

type PositionCardProps = {
	position: JobPosting;
	index: number;
	onApply: () => void;
};

function PositionCard({ position, index, onApply }: PositionCardProps) {
	const t = useLang();
	const catId = getCategoryId(position);
	const CatIcon = categoryIcons[catId];
	const isPartTime = position.type.toLowerCase().includes("скратено");

	return (
		<motion.div
			initial={{ opacity: 0, y: 20 }}
			animate={{ opacity: 1, y: 0 }}
			transition={{ duration: 0.45, delay: index * 0.055 }}
			className="group relative overflow-hidden rounded-sm border border-border bg-card p-6 sm:p-7 shadow-luxury-sm transition-all duration-500 hover:-translate-y-0.5 hover:border-accent/35 hover:shadow-luxury"
		>
			{/* Gold left reveal bar */}
			<div className="absolute inset-y-0 left-0 w-[2px] origin-top scale-y-0 bg-accent transition-transform duration-500 group-hover:scale-y-100" />

			{/* Faint index number */}
			<span className="pointer-events-none absolute right-6 top-5 select-none font-display text-6xl font-light leading-none text-primary/[0.04] transition-colors duration-500 group-hover:text-accent/[0.07]">
				{String(index + 1).padStart(2, "0")}
			</span>

			{/* Category icon */}
			<div className="mb-4 flex items-center gap-2">
				<div className="inline-flex h-7 w-7 items-center justify-center rounded-full bg-primary/[0.05] text-primary/40 transition-all duration-300 group-hover:bg-accent/10 group-hover:text-accent">
					<CatIcon className="h-3.5 w-3.5" />
				</div>
			</div>

			{/* Title */}
			<h3 className="font-display text-xl sm:text-2xl font-normal leading-snug tracking-tight text-primary mb-3">
				{position.title}
			</h3>

			{/* Location */}
			<p className="text-sm text-foreground/65 leading-relaxed mb-5 line-clamp-2">
				{position.location}
			</p>

			{/* Footer row */}
			<div className="flex items-center justify-between pt-4 border-t border-border">
				<span
					className={`inline-flex rounded-full px-2.5 py-1 text-[9px] font-bold uppercase tracking-[0.14em] ${
						isPartTime
							? "bg-secondary text-muted-foreground"
							: "bg-primary/[0.06] text-primary/60"
					}`}
				>
					{isPartTime
						? t.careerPage.positions.partTime
						: t.careerPage.positions.fullTime}
				</span>

				<button
					type="button"
					onClick={onApply}
					className="inline-flex cursor-pointer items-center gap-1.5 rounded-sm bg-accent px-4 py-2 text-[11px] font-semibold uppercase tracking-wider text-primary shadow-gold transition-all duration-300 hover:bg-accent-light hover:shadow-gold-lg"
				>
					{t.careerPage.positions.apply}
				</button>
			</div>
		</motion.div>
	);
}

// ─── Inquiry form ─────────────────────────────────────────────────────────────

function InquiryForm() {
	const t = useLang();
	const lang = useLangKey();
	const [attempted, setAttempted] = useState(false);
	const [name, setName] = useState("");
	const [phone, setPhone] = useState("");
	const [email, setEmail] = useState("");
	const [file, setFile] = useState<File | null>(null);
	const [status, setStatus] = useState<
		"idle" | "loading" | "success" | "error"
	>("idle");

	const req = lang === "en" ? "Required" : "Задолжително";
	const nameErr = attempted && !name;
	const phoneErr = attempted && !phone;
	const emailErr = attempted && !email;
	const fileErr = attempted && !file;

	async function handleSubmit(e: FormEvent<HTMLFormElement>) {
		e.preventDefault();
		setAttempted(true);
		if (!name || !phone || !email || !file) return;
		setStatus("loading");
		try {
			const cvBase64 = await fileToBase64(file);
			await submitApplication({
				data: { name, email, phone, cvBase64, cvName: file.name },
			});
			setStatus("success");
		} catch {
			setStatus("error");
		}
	}

	const inputBase =
		"w-full rounded-sm border bg-white/[0.05] px-4 py-3 text-sm text-white placeholder:text-white/35 focus:outline-none focus:ring-1 transition-all";
	const inputOk =
		"border-white/[0.08] focus:border-accent/40 focus:ring-accent/30";
	const inputErr =
		"border-destructive ring-1 ring-destructive/30 focus:border-destructive focus:ring-destructive/30";

	if (status === "success") {
		return (
			<div className="flex flex-col items-center justify-center py-10 text-center gap-4">
				<div className="flex h-12 w-12 items-center justify-center rounded-full bg-accent/15">
					<Send className="h-5 w-5 text-accent" />
				</div>
				<p className="text-white font-display text-lg">
					{lang === "en" ? "Application submitted!" : "Пријавата е испратена!"}
				</p>
				<p className="text-sm text-white/55 max-w-xs">
					{lang === "en"
						? "We will be in touch soon."
						: "Ќе стапиме во контакт наскоро."}
				</p>
			</div>
		);
	}

	return (
		<>
			<form onSubmit={handleSubmit} noValidate className="space-y-3">
				<div className="grid sm:grid-cols-2 gap-3">
					<div>
						<input
							type="text"
							name="name"
							value={name}
							onChange={(e) => setName(e.target.value)}
							placeholder={`${t.careerPage.inquiry.namePlaceholder} *`}
							className={`${inputBase} ${nameErr ? inputErr : inputOk}`}
						/>
						{nameErr && <p className="text-destructive text-xs mt-1">{req}</p>}
					</div>
					<div>
						<input
							type="tel"
							name="phone"
							value={phone}
							onChange={(e) => setPhone(e.target.value)}
							placeholder={`${t.careerPage.inquiry.phonePlaceholder} *`}
							className={`${inputBase} ${phoneErr ? inputErr : inputOk}`}
						/>
						{phoneErr && <p className="text-destructive text-xs mt-1">{req}</p>}
					</div>
				</div>
				<div>
					<input
						type="email"
						name="email"
						value={email}
						onChange={(e) => setEmail(e.target.value)}
						placeholder={`${t.careerPage.inquiry.emailPlaceholder} *`}
						className={`${inputBase} ${emailErr ? inputErr : inputOk}`}
					/>
					{emailErr && <p className="text-destructive text-xs mt-1">{req}</p>}
				</div>
				<div>
					<label
						className={`flex cursor-pointer items-center gap-4 rounded-sm border border-dashed px-5 py-5 transition-all duration-500 ${fileErr ? "border-destructive bg-destructive/5" : "border-white/[0.1] bg-white/[0.02] hover:border-accent/30 hover:bg-white/[0.04]"}`}
					>
						<div className="flex h-9 w-9 shrink-0 items-center justify-center rounded-sm bg-white/[0.05]">
							<Upload
								className={`h-4 w-4 ${fileErr ? "text-destructive" : "text-white/50"}`}
							/>
						</div>
						<div className="flex-1 min-w-0">
							<span
								className={`block text-sm font-medium ${fileErr ? "text-destructive" : "text-white/70"}`}
							>
								{file ? file.name : `${t.careerPage.inquiry.uploadCv} *`}
							</span>
							<span className="text-[10px] text-white/40">
								{t.careerPage.inquiry.uploadDesc}
							</span>
						</div>
						<input
							type="file"
							name="cv"
							accept=".pdf"
							onChange={(e) => setFile(e.target.files?.[0] ?? null)}
							className="sr-only"
						/>
					</label>
					{fileErr && <p className="text-destructive text-xs mt-1">{req}</p>}
				</div>
				<button
					type="submit"
					disabled={status === "loading"}
					className="inline-flex w-full cursor-pointer items-center justify-center gap-2 rounded-sm bg-accent px-6 py-3.5 text-sm font-semibold text-primary shadow-gold transition-all duration-500 hover:bg-accent-light hover:shadow-gold-lg disabled:opacity-60 disabled:cursor-not-allowed"
				>
					<span>
						{status === "loading"
							? lang === "en"
								? "Sending..."
								: "Се испраќа..."
							: t.careerPage.inquiry.submit}
					</span>
					<Send className="h-3.5 w-3.5" />
				</button>
				{status === "error" && (
					<p className="text-center text-xs text-destructive">
						{lang === "en"
							? "Something went wrong. Please try again."
							: "Се случи грешка. Обидете се повторно."}
					</p>
				)}
			</form>
			<p className="mt-5 text-center text-[10px] tracking-wide text-white/35">
				{t.careerPage.inquiry.privacy}
			</p>
		</>
	);
}

// ─── Apply modal ──────────────────────────────────────────────────────────────

type ModalProps = {
	position: JobPosting;
	onClose: () => void;
};

function ApplyModal({ position, onClose }: ModalProps) {
	const formId = useId();
	const t = useLang();
	const lang = useLangKey();
	const [attempted, setAttempted] = useState(false);
	const [name, setName] = useState("");
	const [email, setEmail] = useState("");
	const [phone, setPhone] = useState("");
	const [file, setFile] = useState<File | null>(null);
	const [status, setStatus] = useState<
		"idle" | "loading" | "success" | "error"
	>("idle");
	const isPartTime = position.type.toLowerCase().includes("скратено");

	const req = lang === "en" ? "Required" : "Задолжително";
	const nameErr = attempted && !name;
	const emailErr = attempted && !email;
	const phoneErr = attempted && !phone;
	const fileErr = attempted && !file;

	async function handleSubmit(e: FormEvent<HTMLFormElement>) {
		e.preventDefault();
		setAttempted(true);
		if (!name || !email || !phone || !file) return;
		setStatus("loading");
		try {
			const cvBase64 = await fileToBase64(file);
			await submitApplication({
				data: {
					name,
					email,
					phone,
					cvBase64,
					cvName: file.name,
					jobPostingDocumentId: position.documentId,
				},
			});
			setStatus("success");
		} catch {
			setStatus("error");
		}
	}

	const inputBase =
		"w-full rounded-sm border bg-background px-4 py-3 text-sm text-primary placeholder:text-muted-foreground focus:outline-none focus:ring-1 transition-all";
	const inputOk = "border-border focus:border-accent/50 focus:ring-accent/30";
	const inputErr =
		"border-destructive ring-1 ring-destructive/30 focus:border-destructive focus:ring-destructive/30";

	useEffect(() => {
		document.body.style.overflow = "hidden";
		return () => {
			document.body.style.overflow = "";
		};
	}, []);

	useEffect(() => {
		function onKey(e: KeyboardEvent) {
			if (e.key === "Escape") onClose();
		}
		document.addEventListener("keydown", onKey);
		return () => document.removeEventListener("keydown", onKey);
	}, [onClose]);

	return (
		<div className="fixed inset-0 z-[100] flex items-end sm:items-center justify-center sm:p-4">
			<motion.div
				initial={{ opacity: 0 }}
				animate={{ opacity: 1 }}
				exit={{ opacity: 0 }}
				transition={{ duration: 0.2 }}
				className="absolute inset-0 bg-primary/70 backdrop-blur-md"
				onClick={onClose}
				role="presentation"
			/>
			<motion.div
				initial={{ opacity: 0, y: 48 }}
				animate={{ opacity: 1, y: 0 }}
				exit={{ opacity: 0, y: 32 }}
				transition={{ duration: 0.32, ease: [0.16, 1, 0.3, 1] }}
				className="relative w-full sm:max-w-5xl bg-card rounded-t-sm sm:rounded-sm shadow-luxury-xl sm:ring-2 sm:ring-white/20 overflow-hidden max-h-[90dvh] flex flex-col"
			>
				<div className="h-[2px] w-full bg-accent shrink-0" />

				<button
					type="button"
					onClick={onClose}
					className="absolute top-4 right-4 z-10 flex h-8 w-8 cursor-pointer items-center justify-center rounded-full bg-white/10 text-white/60 transition-all hover:bg-white/20 hover:text-white sm:bg-secondary sm:text-muted-foreground sm:hover:bg-secondary sm:hover:text-primary"
				>
					<X className="h-4 w-4" />
				</button>

				<div className="flex flex-col sm:flex-row flex-1 min-h-0">
					{/* Left: scrollable job details */}
					<div className="bg-primary px-6 py-6 sm:w-[52%] sm:shrink-0 sm:px-8 sm:py-8 overflow-y-auto">
						<p className="text-[10px] font-bold uppercase tracking-[0.18em] text-accent mb-3">
							{t.careerPage.modal.positionLabel}
						</p>
						<h3 className="font-display text-xl sm:text-2xl font-normal text-white leading-snug mb-3">
							{position.title}
						</h3>
						<span
							className={`inline-flex rounded-full px-2.5 py-1 text-[9px] font-bold uppercase tracking-[0.14em] mb-5 ${
								isPartTime
									? "bg-white/10 text-white/60"
									: "bg-accent/15 text-accent/70"
							}`}
						>
							{isPartTime
								? t.careerPage.modal.partTime
								: t.careerPage.modal.fullTime}
						</span>

						{position.description && (
							<div className="prose prose-slate prose-invert prose-sm max-w-none">
								<ReactMarkdown>{position.description}</ReactMarkdown>
							</div>
						)}
					</div>

					{/* Right: form */}
					<div className="flex-1 border-t border-white/10 sm:border-t-0 sm:border-l sm:border-border flex flex-col">
						<div className="px-6 py-5 sm:px-7 sm:py-6 border-b border-border shrink-0">
							<p className="text-[10px] font-bold uppercase tracking-[0.18em] text-accent mb-1">
								{t.careerPage.modal.applyLabel}
							</p>
							<p className="text-sm text-muted-foreground">
								{t.careerPage.modal.fill}
							</p>
						</div>
						{status === "success" ? (
							<div className="flex flex-col items-center justify-center flex-1 py-12 text-center gap-4 px-6">
								<div className="flex h-12 w-12 items-center justify-center rounded-full bg-accent/10">
									<Send className="h-5 w-5 text-accent" />
								</div>
								<p className="font-display text-lg text-primary">
									{lang === "en"
										? "Application submitted!"
										: "Пријавата е испратена!"}
								</p>
								<p className="text-sm text-muted-foreground max-w-xs">
									{lang === "en"
										? "We will be in touch soon."
										: "Ќе стапиме во контакт наскоро."}
								</p>
							</div>
						) : (
							<form
								id={formId}
								onSubmit={handleSubmit}
								noValidate
								className="px-6 py-5 sm:px-7 sm:py-6 space-y-3 overflow-y-auto"
							>
								<div className="grid grid-cols-1 sm:grid-cols-2 gap-3">
									<div className="sm:col-span-2">
										<input
											type="text"
											name="name"
											value={name}
											onChange={(e) => setName(e.target.value)}
											placeholder={`${t.careerPage.modal.namePlaceholder} *`}
											className={`${inputBase} ${nameErr ? inputErr : inputOk}`}
										/>
										{nameErr && (
											<p className="text-destructive text-xs mt-1">{req}</p>
										)}
									</div>
									<div>
										<input
											type="email"
											name="email"
											value={email}
											onChange={(e) => setEmail(e.target.value)}
											placeholder="Email *"
											className={`${inputBase} ${emailErr ? inputErr : inputOk}`}
										/>
										{emailErr && (
											<p className="text-destructive text-xs mt-1">{req}</p>
										)}
									</div>
									<div>
										<input
											type="tel"
											name="phone"
											value={phone}
											onChange={(e) => setPhone(e.target.value)}
											placeholder={`${t.careerPage.modal.phonePlaceholder} *`}
											className={`${inputBase} ${phoneErr ? inputErr : inputOk}`}
										/>
										{phoneErr && (
											<p className="text-destructive text-xs mt-1">{req}</p>
										)}
									</div>
								</div>
								<div>
									<label
										className={`flex cursor-pointer items-center gap-4 rounded-sm border border-dashed px-5 py-4 transition-all duration-300 ${fileErr ? "border-destructive bg-destructive/5" : "border-primary/12 bg-secondary/40 hover:border-accent/40 hover:bg-secondary/60"}`}
									>
										<div className="flex h-9 w-9 shrink-0 items-center justify-center rounded-sm bg-background shadow-luxury-sm">
											<Upload
												className={`h-4 w-4 ${fileErr ? "text-destructive" : "text-muted-foreground"}`}
											/>
										</div>
										<div className="flex-1 min-w-0">
											<span
												className={`block text-sm font-medium truncate ${fileErr ? "text-destructive" : "text-primary/70"}`}
											>
												{file
													? file.name
													: `${t.careerPage.modal.uploadResume} *`}
											</span>
											<span className="text-[10px] text-muted-foreground">
												{t.careerPage.modal.uploadDesc}
											</span>
										</div>
										<input
											type="file"
											name="cv"
											accept=".pdf"
											onChange={(e) => setFile(e.target.files?.[0] ?? null)}
											className="sr-only"
										/>
									</label>
									{fileErr && (
										<p className="text-destructive text-xs mt-1">{req}</p>
									)}
								</div>
								<button
									type="submit"
									disabled={status === "loading"}
									className="w-full inline-flex cursor-pointer items-center justify-center gap-2 rounded-sm bg-accent py-3.5 text-sm font-semibold text-primary shadow-gold transition-all duration-300 hover:bg-accent-light hover:shadow-gold-lg disabled:opacity-60 disabled:cursor-not-allowed"
								>
									<span>
										{status === "loading"
											? lang === "en"
												? "Sending..."
												: "Се испраќа..."
											: t.careerPage.modal.submit}
									</span>
									<Send className="h-3.5 w-3.5" />
								</button>
								{status === "error" && (
									<p className="text-center text-xs text-destructive">
										{lang === "en"
											? "Something went wrong. Please try again."
											: "Се случи грешка. Обидете се повторно."}
									</p>
								)}
								<p className="text-center text-[10px] text-muted-foreground">
									{t.careerPage.modal.privacy}
								</p>
							</form>
						)}
					</div>
				</div>
			</motion.div>
		</div>
	);
}
